import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { RecommendationService } from '@app/@shared/services/recommendation.service';
import { SorteoType } from '@app/@shared/models/sorteo.model';
import { SorteoService } from '@app/@shared/services/sorteo.service';
import { firstValueFrom } from 'rxjs';
import { GameType } from '@app/@shared/models/game.model';
import { Logger } from '@app/@shared/logger.service';
import {
  ProgolMSRecommendation,
  ProtouchRecommendation,
  ProtouchRecommendedNumbers,
} from '@app/@shared/models/quiniela.model';
import { Recommendation } from '@app/@shared/models/recommendation.model';

const log = new Logger('RecommendationComponent');
@Component({
  selector: 'app-recommendation',
  templateUrl: './recommendation.component.html',
  styleUrls: ['./recommendation.component.scss'],
})
export class RecommendationComponent {
  @Input() limit: number = 56;
  @Input() size: number = 10;
  @Input() gameType?: string;
  @Input() sorteoType?: SorteoType;
  @Input() sorteoS?: SorteoService;
  @Input() isMediaSemana: boolean = false;
  @Output() applyNumbers = new EventEmitter<{ numbers: number[]; category: string }>();
  @Output() applyNumbersProtouch = new EventEmitter<{ selections: any[]; category: string }>();
  @Output() applyNumbersProgolMS = new EventEmitter<{ selections: any[]; category: string; isMediaSemana?: boolean }>();

  recommendationsList: any[] = [];
  recommendedNumbers: number[] = [];
  recommendedSign: string = '';
  recommendedName: string = '';
  trisRecommendations: { category: string; numbers: number[] }[] = [];

  protouchRecommendations: ProtouchRecommendation[] = [];
  progolMSRecommendations: ProgolMSRecommendation[] = [];
  progolRevRecommended: { [key: string]: string } | null = null;
  progolRevRecommendedRec: any | null = null;
  progolRevanchaRecommendations: any[] = [];

  quinielaIndices = Array.from({ length: 14 }, (_, i) => i + 1);
  revanchaIndices = Array.from({ length: 7 }, (_, i) => i + 1);

  isLoading: boolean = false;
  constructor(
    private modalCtrl: ModalController,
    private recommendedS: RecommendationService //  public sorteoS: SorteoService,
  ) {}

  ionViewDidEnter() {
    this.fetchRecommendations();
  }

  async fetchRecommendations() {
    this.isLoading = true;
    const recommendations = await firstValueFrom(
      this.recommendedS.getRecommendationsByGame(this.gameType || this.sorteoType || '')
    );

    if (!recommendations.length) {
      log.warn('⚠️ No hay recomendaciones previas para este juego.');
    }
    if (this.gameType === 'tris') {
      const order = ['Directa 5', 'Directa 4', 'Directa 3', 'Par inicial', 'Par final', 'Inicial', 'Final'];

      // this.trisRecommendations = order
      //   .map((category) => {
      //     const rec = recommendations.find(
      //       (rec) => rec.category && rec.category.toLowerCase() === category.toLowerCase()
      //     );
      //     return rec ? { category: rec.category, numbers: rec.recommendedNumbers } : null;
      //   })
      //   .filter((rec): rec is { category: string; numbers: number[] } => rec !== null);
      this.trisRecommendations = order.flatMap((category) => {
        const matches = recommendations.filter(
          (rec) => rec.category && rec.category.toLowerCase() === category.toLowerCase()
        );
        return matches
          .filter((rec) => rec.category !== undefined)
          .map((rec) => ({
            category: rec.category as string,
            numbers: rec.recommendedNumbers,
          }));
      });
    } else if (this.gameType === 'chispazo' || this.gameType === 'melate') {
      this.trisRecommendations = recommendations
        .sort((a, b) => a.recommendedNumbers.length - b.recommendedNumbers.length)
        .map((r) => {
          return {
            category: `${r.recommendedNumbers.length} números`,
            numbers: [...r.recommendedNumbers.sort((a, b) => a - b)],
          };
        });
    } else if (this.gameType === 'protouch') {
      this.protouchRecommendations = recommendations as unknown as ProtouchRecommendation[];
    } else if (this.gameType === 'progol' && this.isMediaSemana) {
      // Solo Media Semana
      const mediaSemanaRecs = recommendations.filter((r: any) => r.isMediaSemana);
      this.progolMSRecommendations = mediaSemanaRecs as unknown as ProgolMSRecommendation[];
    } else if (this.gameType === 'progol') {
      const revanchaRecsArray = recommendations.filter((r: any) => r.isMediaSemana === false);

      log.info('Found all Revancha recommendations:', revanchaRecsArray);

      this.progolRevanchaRecommendations = revanchaRecsArray;
    } else {
      this.recommendationsList = recommendations.map((r) => ({
        // numbers: r.recommendedNumbers.sort((a, b) => a - b),
        numbers: this.sorteoS?.validGameTypes.includes(this.gameType ?? '')
          ? r.recommendedNumbers
          : r.recommendedNumbers.sort((a, b) => a - b),
        signName: r.signName || null,
        signNumber: r.signNumber || null,
      }));
    }

    this.isLoading = false;
  }

  apply(numbers: number[], category: string, sign?: string) {
    let arrangedNumbers: number[] = Array(this.size).fill(-1);

    if (category === 'Inicial' || category === 'Par inicial') {
      numbers.forEach((num, i) => {
        arrangedNumbers[i] = num;
      });
    } else {
      const startIndex = arrangedNumbers.length - numbers.length;
      numbers.forEach((num, i) => {
        arrangedNumbers[startIndex + i] = num;
      });
    }

    if (this.gameType === 'tris') {
      this.applyNumbers.emit({ numbers: arrangedNumbers, category });
      this.modalCtrl.dismiss({ numbers: arrangedNumbers, category });
    } else if (this.sorteoType === 'zodiaco' || this.sorteoType === 'zodiaco-especial') {
      this.applyNumbers.emit({ numbers: arrangedNumbers, category: sign || '' });
      this.modalCtrl.dismiss({ numbers: arrangedNumbers, category: sign || '' });
    } else {
      // Filtrar los -1 antes de emitir
      arrangedNumbers = arrangedNumbers.filter((num) => num !== -1);

      this.applyNumbers.emit({ numbers: arrangedNumbers, category: '' });
      this.modalCtrl.dismiss(arrangedNumbers);
    }
  }

  applyProtouchRecommendation(recommendation: ProtouchRecommendedNumbers) {
    const selectedValues: string[] = [];

    // Locales (L1–L13)
    for (let i = 1; i <= 13; i++) {
      const key = `L${i}`;
      const value = recommendation[key];
      if (value) selectedValues.push(`${key}-${value}`);
    }

    // Diferencias (D1–D13)
    for (let i = 1; i <= 13; i++) {
      const key = `D${i}`;
      const value = recommendation[key];
      if (value) selectedValues.push(`${key}-${value}`);
    }

    // Visitantes (V1–V13)
    for (let i = 1; i <= 13; i++) {
      const key = `V${i}`;
      const value = recommendation[key];
      if (value) selectedValues.push(`${key}-${value}`);
    }

    const touch = recommendation['touch'] || '';

    this.applyNumbersProtouch.emit({
      selections: selectedValues,
      category: touch,
    });

    this.modalCtrl.dismiss({
      selections: selectedValues,
      category: touch,
    });
  }
  applyProgolMSRecommendation(recommendation: { [key: string]: string }) {
    const selections: string[] = [];

    for (let i = 1; i <= 9; i++) {
      const l = recommendation[`L${i}`];
      const d = recommendation[`D${i}`];
      const v = recommendation[`V${i}`];

      if (l) selections.push(`L${i}-${l}`);
      if (d) selections.push(`D${i}-${d}`);
      if (v) selections.push(`V${i}-${v}`);
    }

    this.applyNumbersProgolMS.emit({
      selections: selections,
      category: 'progol',
      isMediaSemana: this.isMediaSemana,
    });

    this.modalCtrl.dismiss({
      selections,
      category: 'progol',
      isMediaSemana: this.isMediaSemana,
    });
  }
  applyProgolRevanchaRecommendation(rec: {
    recommendedNumbers: { [key: string]: string };
    revRecommended: { [key: string]: string };
  }) {
    const selections: string[] = [];

    // Quiniela Normal (hasta 14 partidos)
    for (let i = 1; i <= 14; i++) {
      const l = rec.recommendedNumbers[`L${i}`];
      const d = rec.recommendedNumbers[`D${i}`];
      const v = rec.recommendedNumbers[`V${i}`];

      if (l) selections.push(`L${i}-${l}`);
      if (d) selections.push(`D${i}-${d}`);
      if (v) selections.push(`V${i}-${v}`);
    }

    // Revancha (hasta 7 partidos)
    for (let i = 1; i <= 7; i++) {
      const lr = rec.revRecommended[`LR${i}`];
      const dr = rec.revRecommended[`DR${i}`];
      const vr = rec.revRecommended[`VR${i}`];

      if (lr) selections.push(`LR${i}-${lr}`);
      if (dr) selections.push(`DR${i}-${dr}`);
      if (vr) selections.push(`VR${i}-${vr}`);
    }

    this.applyNumbersProgolMS.emit({
      selections,
      category: 'progol',
      isMediaSemana: this.isMediaSemana,
    });

    this.modalCtrl.dismiss({
      selections,
      category: 'progol',
      isMediaSemana: this.isMediaSemana,
    });
  }

  getProgolMatchIndices(recommended: { [key: string]: string }): number[] {
    const indicesSet = new Set<number>();

    Object.keys(recommended).forEach((key) => {
      const match = key.match(/^[LDV](\d+)$/); // Coincide con L#, D#, V#
      if (match) {
        indicesSet.add(+match[1]);
      }
    });

    // Devuelve los índices únicos y ordenados
    return Array.from(indicesSet).sort((a, b) => a - b);
  }

  close() {
    this.modalCtrl.dismiss();
  }

  getBubbleClass(gameType?: string, num?: number): string {
    switch (gameType) {
      case 'tris':
        return `bubble-tris${typeof num === 'number' && num < 0 ? ' disable' : ''}`;
      case 'gana-gato':
        return 'bubble-gana-gato';
      case 'melate':
        return 'bubble-melate';
      case 'chispazo':
        return 'bubble-chispazo';
      case 'gordito':
        return 'bubble-gordito';
      case 'zodiaco':
        return 'bubble-zodiaco';
      case 'zodiaco-especial':
        return 'bubble-zodiaco-especial';
      case 'mayor':
        return 'bubble-mayor';
      case 'magno':
        return 'bubble-magno';
      case 'superior':
        return 'bubble-superior';
      case 'especial':
        return 'bubble-especial';
      case 'gran-especial':
        return 'bubble-gran-especial';
      default:
        return 'bubble-default';
    }
  }
  getZodiacIcon(signName: string): string {
    const icons: { [key: string]: string } = {
      ARIES: 'assets/icons/zodiac/game-icons_aries.svg',
      TAURO: 'assets/icons/zodiac/game-icons_taurus.svg',
      GÉMINIS: 'assets/icons/zodiac/game-icons_gemini.svg',
      CÁNCER: 'assets/icons/zodiac/game-icons_cancer.svg',
      LEO: 'assets/icons/zodiac/game-icons_leo.svg',
      VIRGO: 'assets/icons/zodiac/game-icons_virgo.svg',
      LIBRA: 'assets/icons/zodiac/game-icons_libra.svg',
      ESCORPIÓN: 'assets/icons/zodiac/game-icons_scorpio.svg',
      SAGITARIO: 'assets/icons/zodiac/game-icons_sagittarius.svg',
      CAPRICORNIO: 'assets/icons/zodiac/game-icons_capricorn.svg',
      ACUARIO: 'assets/icons/zodiac/game-icons_aquarius.svg',
      PISCIS: 'assets/icons/zodiac/game-icons_pisces.svg',
    };

    const key = signName.toUpperCase();
    return icons[key] || 'assets/icons/zodiac/default.svg';
  }
  formatZodiacName(signName: string): string | undefined {
    const signos = this.sorteoS?.signosZodiaco;

    if (!signos) return;

    const normalizar = (str: string) =>
      str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

    const nombreNormalizado = normalizar(signName);

    const encontrado = signos.find((signo) => normalizar(signo.nombreSigno) === nombreNormalizado);

    if (!encontrado) return;

    const nombre = encontrado.nombreSigno.toLowerCase();
    return nombre.charAt(0).toUpperCase() + nombre.slice(1);
  }

  convertToNumbers(recommendedNumbers: { [key: string]: string }): number[] {
    return Object.values(recommendedNumbers).map((value) => {
      const num = parseInt(value, 10);
      return isNaN(num) ? -1 : num;
    });
  }
}
